import React, { useEffect, useState } from 'react';
import { Button, Typography, Stack, Grid, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../hooks';
import { TP_COLORS } from '../../../constants';
import { getOnboardingUserIdFromLocalStorage } from '../../../helper';
import {
  getOnboardingState,
  saveOnboarding,
} from '../../../store/V2/OnboardingSlice';
import { actionButtonSelectStyles, actionButtonStyles } from './Step2';
import { backButtonProps } from './Step1';
import CustomStepper from './components/CustomStepper';

type Step5Props = {
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
};

const Step5: React.FC<Step5Props> = ({ onNext, onBack, currentStep }) => {
  const dispatch = useDispatch();

  // Store
  const { onboardingData, status: onboardingStatus } = useAppSelector(
    getOnboardingState
  );
  const onboardingUserId = getOnboardingUserIdFromLocalStorage();

  // State
  const [selectedLearningStyle, setSelectedLearningStyle] = useState<
    string | null
  >(onboardingData?.learningStyle || null);

  const isLoading = onboardingStatus === 'loading';

  const buttonOptions = [
    'I like visual explanations and diagrams',
    'I prefer practice questions and quizzes',
    'I need step-by-step guidance',
    'I’m not sure yet',
  ];

  useEffect(() => {
    if (onboardingData?.learningStyle) {
      setSelectedLearningStyle(onboardingData.learningStyle);
    }
  }, [onboardingData?.learningStyle]);

  const handleContinue = async (learningStyle: string) => {
    setSelectedLearningStyle(learningStyle);
    await dispatch(
      saveOnboarding({
        _id: onboardingUserId,
        learningStyle,
      })
    );

    onNext();
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      {/* Left section */}
      <Grid
        item
        xs={12}
        sm={4}
        md={6}
        sx={{
          backgroundColor: '#EDF9FE',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Button {...backButtonProps} onClick={onBack}>
          Back
        </Button>
        <img
          src={`${process.env.PUBLIC_URL}/images/onboarding/img-step5.png`}
          alt="step5"
        />
      </Grid>

      {/* Right section */}
      <Grid
        item
        xs={12}
        sm={8}
        md={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Mobile Back button */}
        <Box display={{ xs: 'block', sm: 'none' }}>
          <Button
            {...backButtonProps}
            sx={{ ...backButtonProps.sx, left: '20px', top: '20px' }}
            onClick={onBack}
          />
        </Box>

        {/* Progress bar */}
        <CustomStepper activeStep={currentStep} />

        <Stack
          spacing={8}
          sx={{
            maxWidth: 375,
            width: '100%',
          }}
        >
          <Typography
            fontSize="25px"
            fontWeight="700"
            textAlign="center"
            color={TP_COLORS.gray.darken}
          >
            What’s your learning style?
          </Typography>

          <Stack spacing={2}>
            {buttonOptions.map((option) => (
              <LoadingButton
                key={option}
                sx={
                  !isLoading && selectedLearningStyle === option
                    ? actionButtonSelectStyles
                    : actionButtonStyles
                }
                onClick={() => handleContinue(option)}
                loading={isLoading && selectedLearningStyle === option}
              >
                {option}
              </LoadingButton>
            ))}
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Step5;
